import type { Middleware } from "@reduxjs/toolkit";

/**
 * Redux middleware that exposes window.clinicId for the Home Health mobile app.
 *
 * WHY THIS EXISTS:
 * The Home Health mobile app (https://github.com/circadiatech/copilot) runs CIP in an
 * Android WebView. Due to browser security restrictions, the WebView cannot access
 * localStorage directly. The Android app needs the clinic ID to fetch patients.
 *
 * WHY WE CAN'T USE store.subscribe():
 * When the user changes clinics, the app reloads. On reload:
 *   1. redux-persist rehydrates the OLD clinic ID from localStorage (stale data)
 *   2. API calls fetch the NEW clinic ID from notification settings
 *   3. setUserClinic is dispatched with the CORRECT clinic ID (1-5 seconds later)
 *
 * If we used store.subscribe(), we'd expose the stale rehydrated value during that
 * 1-5 second window, which would cause the Android app to fetch patients for the
 * WRONG clinic.
 *
 * HOW THIS WORKS:
 * We listen specifically for the `clinic/setUserClinic` action. This action is only
 * dispatched when the clinic is definitively known (after API response), not during
 * rehydration. This ensures the Android app is only informed when we have the correct value.
 *
 * USAGE BY ANDROID:
 * See the implementation in AuthJavaScriptInterface.kt
 */

export const clinicIdMiddleware: Middleware = () => (next) => (action) => {
  // Let the action proceed first
  const result = next(action);

  // After the action is processed, check if it was setUserClinic
  if (action.type === "clinic/setUserClinic") {
    const clinicId = action.payload?.clinic?.id ?? null;
    // Notify Android app via Auth bridge if available
    if (
      typeof window !== "undefined" &&
      (window as any).AndroidAuth?.setClinicId
    ) {
      try {
        (window as any).AndroidAuth.setClinicId(clinicId);
      } catch (error) {
        console.warn(
          "Failed to notify Android app of clinic ID change:",
          error
        );
      }
    }
  }

  return result;
};
